import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogoMark } from "@/components/Logo";
import { BracketButton } from "@/components/atoms";
import { PORTAL_NAME } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ["Cartridge Heaters", "Band Heaters", "Flexible Heaters", "Strip Heaters", "Air Heaters", "Coil Heaters", "Box Heaters", "Thermocouples"];
const STYLE_MAP = { Cartridge: "Cartridge Heaters", Band: "Band Heaters", Strip: "Strip Heaters" };

function CalculatorPanel({ products, addToCart }) {
  const [style, setStyle] = useState("Cartridge");
  const [dia, setDia] = useState(0.5);
  const [len, setLen] = useState(6);
  const [width, setWidth] = useState(2);
  const [watts, setWatts] = useState(500);
  const [volts, setVolts] = useState(240);

  const r = useMemo(() => {
    const w = +watts || 0, v = +volts || 1;
    const amps = w / v;
    const res = w ? (v * v) / w : 0;
    let wd = 0, rpi = 0;
    if (style === "Cartridge") {
      const hl = Math.max(+len - 1.25, 0.1);
      wd = w / (3.14 * (+dia || 0.1) * hl); rpi = res / hl;
    } else if (style === "Band") {
      const area = 3.14 * (+dia || 0.1) * (+len || 1);
      wd = w / area; rpi = res / area;
    } else {
      const area = Math.max((+len - 0.25) * (+width - 0.25), 0.1);
      wd = w / area; rpi = res / area;
    }
    return { amps, res, wd, rpi };
  }, [style, dia, len, width, watts, volts]);

  const hot = r.wd > 40;
  const matches = products.filter((p) => p.category === STYLE_MAP[style]).slice(0, 3);
  const num = (x, d = 1) => (isFinite(x) ? x.toFixed(d) : "—");

  return (
    <div className="border border-ember/40 bg-white rounded-lg p-6" data-testid="calc-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-ember">ENGINEERING CALCULATOR — WATT DENSITY</p>
        <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-dim">CALCULATE → MATCH → QUOTE</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <label className="block">
          <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-dim block mb-1.5">STYLE</span>
          <select value={style} onChange={(e) => setStyle(e.target.value)} data-testid="calc-style"
            className="w-full bg-bone border border-linec rounded-md px-3 py-2.5 text-sm text-navy focus:outline-none focus:border-ember">
            {Object.keys(STYLE_MAP).map((s) => <option key={s}>{s}</option>)}
          </select>
        </label>
        {style !== "Strip" && (
          <label className="block">
            <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-dim block mb-1.5">DIA (IN)</span>
            <input type="number" step="0.125" min="0.1" value={dia} onChange={(e) => setDia(e.target.value)} data-testid="calc-dia"
              className="w-full bg-bone border border-linec rounded-md px-3 py-2.5 text-sm text-navy focus:outline-none focus:border-ember" />
          </label>
        )}
        <label className="block">
          <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-dim block mb-1.5">{style === "Band" ? "WIDTH (IN)" : "LENGTH (IN)"}</span>
          <input type="number" step="0.25" min="0.5" value={style === "Band" ? len : len} onChange={(e) => setLen(e.target.value)} data-testid="calc-length"
            className="w-full bg-bone border border-linec rounded-md px-3 py-2.5 text-sm text-navy focus:outline-none focus:border-ember" />
        </label>
        {style === "Strip" && (
          <label className="block">
            <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-dim block mb-1.5">WIDTH (IN)</span>
            <input type="number" step="0.25" min="0.5" value={width} onChange={(e) => setWidth(e.target.value)} data-testid="calc-width"
              className="w-full bg-bone border border-linec rounded-md px-3 py-2.5 text-sm text-navy focus:outline-none focus:border-ember" />
          </label>
        )}
        <label className="block">
          <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-dim block mb-1.5">WATTS</span>
          <input type="number" min="1" value={watts} onChange={(e) => setWatts(e.target.value)} data-testid="calc-watts"
            className="w-full bg-bone border border-linec rounded-md px-3 py-2.5 text-sm text-navy focus:outline-none focus:border-ember" />
        </label>
        <label className="block">
          <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-dim block mb-1.5">VOLTS</span>
          <input type="number" min="1" value={volts} onChange={(e) => setVolts(e.target.value)} data-testid="calc-volts"
            className="w-full bg-bone border border-linec rounded-md px-3 py-2.5 text-sm text-navy focus:outline-none focus:border-ember" />
        </label>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
        {[
          ["AMPS", num(r.amps, 2), "calc-readout-amps"],
          ["RESISTANCE Ω", num(r.res, 2), "calc-readout-res"],
          ["RPI Ω/IN", num(r.rpi, 2), "calc-readout-rpi"],
          ["WATT DENSITY W/IN²", num(r.wd, 1), "calc-readout-wd"],
        ].map(([l, v, t]) => (
          <div key={l} className="bg-bone border border-linec rounded-md px-4 py-3">
            <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-dim">{l}</p>
            <p className="font-mono text-lg font-bold text-navy mt-1" data-testid={t}>{v}</p>
          </div>
        ))}
      </div>
      {hot && (
        <p className="mt-4 font-mono text-[11px] text-ember border border-ember/50 bg-ember/5 rounded-md px-4 py-2.5" data-testid="calc-warning">
          Watt density above 40 W/in² — review the application with engineering before placing an order.
        </p>
      )}

      <div className="mt-6 pt-5 border-t border-linec">
        <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-dim mb-3">MATCHED IN THE CATALOG — QUOTE IN ONE CLICK</p>
        <div className="grid sm:grid-cols-3 gap-3">
          {matches.map((p) => (
            <div key={p.sku} className="border border-linec rounded-md p-3.5 flex items-center justify-between gap-2" data-testid={`calc-matched-${p.sku}`}>
              <div>
                <p className="font-mono text-[10px] text-dim">{p.sku}</p>
                <p className="text-xs font-semibold text-navy mt-0.5 leading-snug">{p.name}</p>
              </div>
              <button onClick={() => addToCart(p)} data-testid={`portal-add-to-rfq-${p.sku}`}
                className="font-mono text-[9px] tracking-[0.1em] uppercase bg-ember text-white font-bold px-2.5 py-1.5 rounded-md hover:bg-glow transition-colors shrink-0">
                + RFQ
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function PortalDemo() {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [category, setCategory] = useState("");
  const [maxTemp, setMaxTemp] = useState(1600);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  const [cart, setCart] = useState([]);
  const [drawer, setDrawer] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [contact, setContact] = useState({ name: "", company: "", email: "", phone: "" });
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/products`, { params: { q: debounced, category, max_temp: maxTemp } })
      .then((r) => setProducts(r.data.products))
      .catch(() => toast.error("Could not load the catalog. Is the backend awake?"))
      .finally(() => setLoading(false));
  }, [debounced, category, maxTemp]);

  useEffect(() => {
    axios.get(`${API}/rfq/stats`).then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const totalQty = useMemo(() => cart.reduce((s, i) => s + i.qty, 0), [cart]);

  const addToCart = (p) => {
    if (cart.some((i) => i.product_id === p.sku)) { toast("Already in this RFQ", { description: p.sku }); return; }
    setCart((c) => [...c, { product_id: p.sku, name: p.name, qty: 1, note: "" }]);
    toast.success(`Added ${p.sku}`, { description: p.name });
  };

  const setQty = (sku, d) =>
    setCart((c) => c.map((i) => (i.product_id === sku ? { ...i, qty: Math.max(1, i.qty + d) } : i)));

  const submitRfq = async () => {
    if (!contact.name.trim() || !contact.email.includes("@")) {
      toast.error("Name and a valid work email are required"); return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/rfq`, { contact, items: cart, message });
      setSubmitted(data);
      toast.success(`RFQ ${data.reference} submitted`, { description: data.status });
      setStats((s) => s ? { ...s, total_rfqs: s.total_rfqs + 1 } : s);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed — try again");
    } finally { setSending(false); }
  };

  const resetRfq = () => { setSubmitted(null); setCart([]); setDrawer(false); setContact({ name: "", company: "", email: "", phone: "" }); setMessage(""); };

  return (
    <main className="grain bg-bone min-h-screen pb-40 text-navy">
      {/* ribbon */}
      <div className="border-b border-linec bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-14 flex items-center justify-between gap-4">
          <Link to="/" data-testid="demo-back-link" className="flex items-center gap-3 shrink-0">
            <LogoMark size={30} />
            <span className="hidden sm:inline font-mono text-[10px] tracking-[0.25em] uppercase text-dim">&larr; Back to proposal</span>
          </Link>
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-navy/70 truncate" data-testid="demo-prototype-badge">WORKING PROTOTYPE — {PORTAL_NAME}™ PORTAL</p>
          <p className="hidden md:inline font-mono text-[10px] tracking-[0.2em] uppercase text-dim" data-testid="demo-stats-line">
            {stats ? `${stats.total_products} SKUs · ${stats.total_rfqs} RFQs in demo` : "…"}
          </p>
        </div>
      </div>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 lg:pt-20">
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-ember">THIS REPLACES THE IMDB SERVER</p>
        <h1 className="font-body font-medium tracking-[-0.035em] leading-[1.02] text-4xl sm:text-5xl lg:text-6xl mt-5">
          Find your heater.<br /><span className="text-ember">Quote it in 60 seconds.</span>
        </h1>
        <p className="text-mutedx text-lg mt-6 max-w-2xl leading-relaxed">
          Sample catalog loaded with Marathon's product families and spec ceilings. Customers search and filter on
          their own; the RFQ lands with sales — and as a deal in HubSpot — the moment it's sent.
        </p>

        <div className="mt-10">
          <CalculatorPanel products={products} addToCart={addToCart} />
        </div>

        {/* controls */}
        <div className="mt-12 grid lg:grid-cols-[1fr_auto] gap-6 items-end">
          <div className="space-y-5">
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="portal-search-input"
              placeholder="Search by name, SKU or spec — try “mica” or “1600”"
              className="w-full bg-white border border-linec rounded-md px-5 py-4 text-navy placeholder:text-dim focus:outline-none focus:border-ember transition-colors" />
            <div className="flex flex-wrap gap-2" data-testid="portal-category-filter">
              <button onClick={() => setCategory("")} data-testid="portal-category-chip-all"
                className={`font-mono text-[10px] tracking-[0.15em] uppercase px-3.5 py-2 rounded-md border transition-colors ${!category ? "bg-ember text-white border-ember font-bold" : "border-linec text-mutedx hover:text-ember hover:border-ember"}`}>All</button>
              {CATEGORIES.map((c) => (
                <button key={c} onClick={() => setCategory(category === c ? "" : c)} data-testid={`portal-category-chip-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`font-mono text-[10px] tracking-[0.15em] uppercase px-3.5 py-2 rounded-md border transition-colors ${category === c ? "bg-ember text-white border-ember font-bold" : "border-linec text-mutedx hover:text-ember hover:border-ember"}`}>{c}</button>
              ))}
            </div>
          </div>
          <div className="min-w-[260px] border border-linec bg-white p-4">
            <div className="flex items-center justify-between gap-4 mb-3">
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-dim">MAX TEMP RATING</p>
              <p className="font-mono text-sm font-bold text-ember" data-testid="portal-temp-readout">&le; {maxTemp}°F</p>
            </div>
            <input type="range" min="400" max="1600" step="50" value={maxTemp}
              onChange={(e) => setMaxTemp(+e.target.value)} className="thermal-slider w-full"
              data-testid="portal-temp-slider" aria-label="Maximum temperature filter" />
          </div>
        </div>
      </section>

      {/* results */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-10">
        {loading ? (
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-dim py-20 text-center" data-testid="portal-loading">LOADING CATALOG…</p>
        ) : products.length === 0 ? (
          <div className="border border-linec bg-white py-20 text-center" data-testid="portal-empty-state">
            <p className="font-body font-semibold text-xl text-navy">No heaters match that spec.</p>
            <p className="text-mutedx text-sm mt-2">Loosen the temperature filter or clear the search.</p>
            <button onClick={() => { setQ(""); setCategory(""); setMaxTemp(1600); }} data-testid="portal-clear-filters"
              className="mt-6 font-mono text-xs tracking-[0.2em] uppercase text-ember border border-ember rounded-md px-5 py-2.5 hover:bg-ember hover:text-white transition-colors">
              Clear all filters
            </button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="portal-results-grid">
            {products.map((p) => (
              <article key={p.sku} data-testid={`portal-product-card-${p.sku}`}
                className="border border-linec bg-white ember-hover flex flex-col">
                <div className="relative h-40 overflow-hidden">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                  <span className="absolute top-3 left-3 font-mono text-[9px] tracking-[0.2em] uppercase bg-white/90 backdrop-blur px-2.5 py-1 rounded-sm text-navy">{p.category}</span>
                  <span className="absolute bottom-3 right-3 font-mono text-[10px] font-bold text-white bg-ember px-2.5 py-1 rounded-sm">{p.temp_max_f}°F</span>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <p className="font-mono text-[10px] tracking-[0.2em] text-dim">{p.sku}</p>
                  <h3 className="font-body font-semibold text-base mt-1.5 leading-snug text-navy">{p.name}</h3>
                  <p className="text-mutedx text-xs mt-2 leading-relaxed flex-1">{p.description}</p>
                  <div className="flex items-center justify-between gap-2 mt-4 pt-4 border-t border-linec">
                    <span className="font-mono text-[10px] text-dim">{p.voltage}{p.watt_density ? ` · ${p.watt_density}` : ""}</span>
                    <button onClick={() => addToCart(p)} data-testid={`portal-add-to-rfq-${p.sku}`}
                      className="font-mono text-[10px] tracking-[0.15em] uppercase bg-ember text-white font-bold px-3.5 py-2 rounded-md hover:bg-glow transition-colors">
                      + RFQ
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* cart bar */}
      {cart.length > 0 && !drawer && (
        <div className="fixed bottom-0 inset-x-0 z-40 border-t border-ember bg-navy" data-testid="portal-rfq-cart-bar">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-paper" data-testid="portal-cart-count">
              RFQ LIST: {cart.length} LINE{cart.length > 1 ? "S" : ""} · {totalQty} PCS
            </p>
            <button onClick={() => setDrawer(true)} data-testid="portal-rfq-open-btn"
              className="bg-ember text-white font-body font-semibold text-sm px-7 py-3 rounded-md hover:bg-glow transition-colors">
              Review &amp; send RFQ &rarr;
            </button>
          </div>
        </div>
      )}

      {/* drawer */}
      {drawer && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="portal-rfq-drawer">
          <div className="absolute inset-0 bg-navy/50 backdrop-blur-sm" onClick={() => setDrawer(false)} />
          <aside className="relative w-full sm:w-[460px] h-full bg-bone border-l border-linec overflow-y-auto">
            <div className="sticky top-0 bg-white/95 backdrop-blur border-b border-linec px-6 py-5 flex items-center justify-between">
              <h2 className="font-body font-semibold uppercase text-lg text-navy">{submitted ? "RFQ sent" : "Request for quote"}</h2>
              <button onClick={() => setDrawer(false)} data-testid="portal-close-drawer" className="font-mono text-xs text-dim hover:text-ember transition-colors">CLOSE ✕</button>
            </div>
            <div className="p-6 space-y-6">
              {submitted ? (
                <div className="text-center py-10" data-testid="portal-rfq-success">
                  <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-dim">REFERENCE</p>
                  <p className="font-body font-semibold text-3xl text-ember mt-3" data-testid="portal-rfq-reference">{submitted.reference}</p>
                  <p className="text-mutedx text-sm mt-6 leading-relaxed">
                    {submitted.items} line item{submitted.items > 1 ? "s" : ""} · {submitted.status}.
                    In production this RFQ would create a HubSpot contact + deal and notify the sales team instantly.
                  </p>
                  <button onClick={resetRfq} data-testid="portal-rfq-reset-btn"
                    className="mt-8 bg-ember text-white font-body font-semibold text-sm px-7 py-3.5 rounded-md hover:bg-glow transition-colors w-full">
                    Build another RFQ
                  </button>
                </div>
              ) : (
                <>
                  {cart.map((i) => (
                    <div key={i.product_id} className="border border-linec bg-white p-4" data-testid={`portal-rfq-item-${i.product_id}`}>
                      <div className="flex justify-between gap-3">
                        <div>
                          <p className="font-mono text-[10px] text-dim">{i.product_id}</p>
                          <p className="text-sm font-semibold mt-1 text-navy">{i.name}</p>
                        </div>
                        <button onClick={() => setCart((c) => c.filter((x) => x.product_id !== i.product_id))} data-testid={`portal-rfq-remove-${i.product_id}`}
                          className="font-mono text-[10px] text-dim hover:text-ruby transition-colors">REMOVE</button>
                      </div>
                      <div className="flex items-center gap-3 mt-3">
                        <button onClick={() => setQty(i.product_id, -1)} data-testid={`portal-rfq-qty-minus-${i.product_id}`} className="w-8 h-8 border border-linec rounded-md hover:border-ember transition-colors text-navy">−</button>
                        <span className="font-mono text-sm w-10 text-center text-navy" data-testid={`portal-rfq-qty-${i.product_id}`}>{i.qty}</span>
                        <button onClick={() => setQty(i.product_id, 1)} data-testid={`portal-rfq-qty-plus-${i.product_id}`} className="w-8 h-8 border border-linec rounded-md hover:border-ember transition-colors text-navy">+</button>
                        <span className="font-mono text-[10px] text-dim uppercase tracking-widest">pcs</span>
                      </div>
                    </div>
                  ))}
                  <div className="space-y-3">
                    {[
                      { k: "name", ph: "Full name *", test: "portal-contact-name", type: "text" },
                      { k: "company", ph: "Company", test: "portal-contact-company", type: "text" },
                      { k: "email", ph: "Work email *", test: "portal-contact-email", type: "email" },
                      { k: "phone", ph: "Phone", test: "portal-contact-phone", type: "tel" },
                    ].map((f) => (
                      <input key={f.k} type={f.type} placeholder={f.ph} value={contact[f.k]}
                        onChange={(e) => setContact((c) => ({ ...c, [f.k]: e.target.value }))} data-testid={f.test}
                        className="w-full bg-white border border-linec rounded-md px-4 py-3 text-sm text-navy placeholder:text-dim focus:outline-none focus:border-ember transition-colors" />
                    ))}
                    <textarea placeholder="Application notes (temperatures, dimensions, quantities…)" value={message}
                      onChange={(e) => setMessage(e.target.value)} rows="3" data-testid="portal-contact-message"
                      className="w-full bg-white border border-linec rounded-md px-4 py-3 text-sm text-navy placeholder:text-dim focus:outline-none focus:border-ember transition-colors" />
                  </div>
                  <BracketButton onClick={submitRfq} disabled={sending} primary testid="portal-rfq-submit-btn" className="w-full justify-center">
                    {sending ? "Sending…" : `Send RFQ — ${cart.length} line${cart.length > 1 ? "s" : ""}`}
                  </BracketButton>
                  <p className="font-mono text-[10px] text-dim text-center tracking-[0.15em] uppercase">
                    Routes to sales + HubSpot · No account needed
                  </p>
                </>
              )}
            </div>
          </aside>
        </div>
      )}
    </main>
  );
}
